import {
  createEmptySubApp,
  subAppToPayload,
  type SubApp,
  type SubAppPayload,
} from '@/types/sub-app-types'
import { axiosClient } from '@/utils/axios-client'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useFileStore } from '@/store/file.store'
import type { UUID } from 'node:crypto'

export const useSubAppStore = defineStore('subAppStore', () => {
  const fileStore = useFileStore()
  const subApps = ref<SubApp[]>([])
  const subApp = ref<SubApp>(createEmptySubApp())

  async function fetchSubApps() {
    if (subApps.value.length > 0) return subApps.value

    const response = await axiosClient.request<SubApp[]>({
      method: 'GET',
      url: '/sub-apps',
    })

    await downloadAllFiles(response.data)
    subApps.value = response.data

    return response.data
  }

  async function downloadAllFiles(subApps: SubApp[]) {
    const promises = []

    for (const subApp of subApps) {
      promises.push(downloadFile(subApp))
    }

    await Promise.all(promises)
  }

  async function downloadFile(subApp: SubApp) {
    try {
      const { imageSrc } = await fileStore.downloadFile(subApp.imageId)
      subApp.imageSrc = imageSrc
    } catch {}
  }

  async function fetchSubApp(id: string): Promise<SubAppPayload> {
    const response = await axiosClient.request<SubApp>({
      method: 'GET',
      url: `/sub-apps/${id}`,
    })

    let subAppBlob

    // TODO: catch more specific errors (wait for Stijn)
    try {
      const { imageSrc, blob } = await fileStore.downloadFile(response.data.imageId)
      subApp.value.imageSrc = imageSrc
      subAppBlob = blob
    } catch {}

    subApp.value = { ...response.data, imageSrc: subApp.value.imageSrc }
    return subAppToPayload(subApp.value, subAppBlob)
  }

  async function createSubApp(subAppPayload: SubAppPayload, imageId: string) {
    const response = await axiosClient.request({
      method: 'POST',
      url: 'sub-apps',
      data: {
        ...subAppPayload,
        imageId: imageId,
      },
    })

    const { imageSrc } = await fileStore.downloadFile(imageId)
    response.data.body.imageSrc = imageSrc

    subApps.value = [...subApps.value, response.data.body]
    return response.data.body
  }

  async function patchSubApp(id: UUID, payload: Partial<SubAppPayload>) {
    await axiosClient.request({ method: 'Patch', url: `sub-apps/${id}`, data: payload })
  }

  async function updateSubApp(subAppPayload: SubAppPayload) {
    await axiosClient.request<SubAppPayload>({
      method: 'Patch',
      url: `sub-apps/${subAppPayload.id}`,
      data: {
        ...subAppPayload,
        imageId: subAppPayload.imageId,
      },
    })

    const subApp = subApps.value.find(subApp => subApp.id === subAppPayload.id)
    if (!subApp) return

    const { imageSrc } = await fileStore.downloadFile(subAppPayload.imageId)
    subApp.imageSrc = imageSrc
  }

  async function deleteSubApp(id: UUID) {
    await axiosClient.request({
      method: 'DELETE',
      url: `sub-apps/${id}`,
    })

    subApps.value = subApps.value.filter(app => app.id !== id)
  }

  async function uploadTranslations(id: UUID, file: File) {
    const jsonFile = new File([file], file.name, { type: 'application/json' })

    const formData = new FormData()
    formData.append('file', jsonFile)

    await axiosClient.request({
      method: 'PATCH',
      url: `sub-apps/${id}/translations`,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    })
  }

  async function downloadTranslations(id: UUID): Promise<{ blob: Blob; fileName: string }> {
    const response = await axiosClient.request<Blob>({
      method: 'GET',
      url: `sub-apps/${id}/translations`,
      responseType: 'blob',
    })

    const disposition = response.headers['content-disposition'] as string | undefined
    const fileName = disposition?.match(/filename="?([^"]+)"?/)?.[1] ?? 'translations.json'

    return { blob: response.data, fileName }
  }

  async function deleteTranslations(id: UUID) {
    await axiosClient.request({
      method: 'DELETE',
      url: `sub-apps/${id}/translations`,
    })
  }

  return {
    subApp,
    subApps,
    createSubApp,
    fetchSubApp,
    fetchSubApps,
    updateSubApp,
    patchSubApp,
    deleteSubApp,
    uploadTranslations,
    downloadTranslations,
    deleteTranslations,
  }
})
